// src/AdminDashboard.js
import React, { useEffect, useState } from "react";
import { auth, db, functions } from "./firebase";
import { signOut } from "firebase/auth";
import {
  collection,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { useNavigate, Link } from "react-router-dom";
import SuspendedModal from "./SuspendedModal";
import { httpsCallable } from "firebase/functions";

const USERS_PER_PAGE = 10;
const currencyOptions = ["ZAR", "USD", "EUR", "GBP", "NGN", "KES", "INR"];

const AdminDashboard = () => {
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [loadingAuth, setLoadingAuth] = useState(true);
  const [isSuspendedOpen, setIsSuspendedOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [now, setNow] = useState(new Date());
  const [contactValues, setContactValues] = useState({});

  const navigate = useNavigate();

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const fetchUsers = async () => {
    const snapshot = await getDocs(query(collection(db, "users"), orderBy("email")));
    const userList = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    setUsers(userList);
  };

  const checkSuspended = (userData) => {
    if (userData?.suspended) setIsSuspendedOpen(true);
  };

  useEffect(() => {
    const unsubscribeAuth = auth.onAuthStateChanged(async (user) => {
      if (!user) {
        setLoadingAuth(false);
        navigate("/login");
        return;
      }
      const userRef = doc(db, "users", user.uid);
      const userSnap = await getDoc(userRef);
      const userData = userSnap.exists() ? userSnap.data() : null;
      if (!userData || userData.role !== "admin") {
        navigate("/");
        return;
      }
      setCurrentUser(userData);
      checkSuspended(userData);
      fetchUsers().finally(() => setLoadingAuth(false));
    });
    return () => unsubscribeAuth();
  }, [navigate]);

  const assignPlan = async (userId, plan) => {
    await updateDoc(doc(db, "users", userId), { plan });
    fetchUsers();
  };

  const updateCurrency = async (userId, currency) => {
    await updateDoc(doc(db, "users", userId), { currency });
    fetchUsers();
  };

  const toggleSuspension = async (userId, currentStatus) => {
    await updateDoc(doc(db, "users", userId), { suspended: !currentStatus });
    fetchUsers();
  };

  const toggleCampaign = async (userId, currentStatus) => {
    const userRef = doc(db, "users", userId);
    if (!currentStatus) {
      const startDate = new Date();
      const endDate = new Date(startDate.getTime() + 180 * 24 * 60 * 60 * 1000);
      await updateDoc(userRef, {
        campaignActive: true,
        campaignStart: startDate,
        campaignEnd: endDate,
      });
    } else {
      await updateDoc(userRef, {
        campaignActive: false,
        campaignStart: null,
        campaignEnd: null,
      });
    }
    fetchUsers();
  };

  const handleSetContacts = async (userId) => {
    const value = parseInt(contactValues[userId], 10);
    if (!isNaN(value)) {
      await updateDoc(doc(db, "users", userId), { contactsRemaining: value });
      fetchUsers();
      setContactValues((prev) => ({ ...prev, [userId]: "" }));
    }
  };

  // Admin manual boost helpers
  const deriveExpiry = (user) => {
    const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
    const fromExpiresAt = user?.boostExpiresAt?.toDate
      ? user.boostExpiresAt.toDate()
      : user?.boostExpiresAt
      ? new Date(user.boostExpiresAt)
      : null;
    const fromBoostedUntil = user?.boostedUntil?.toDate
      ? user.boostedUntil.toDate()
      : user?.boostedUntil?.seconds
      ? new Date(user.boostedUntil.seconds * 1000)
      : user?.boostedUntil
      ? new Date(user.boostedUntil)
      : null;
    const fromBoostedAtPlus7 = user?.boostedAt?.toDate
      ? new Date(user.boostedAt.toDate().getTime() + sevenDaysMs)
      : user?.boostedAt?.seconds
      ? new Date(user.boostedAt.seconds * 1000 + sevenDaysMs)
      : null;
    const candidates = [fromExpiresAt, fromBoostedUntil, fromBoostedAtPlus7].filter(Boolean);
    if (candidates.length === 0) return null;
    candidates.sort((a, b) => b - a);
    return candidates[0];
  };

  const adminBoost = async (user, days = 7) => {
    const now = new Date();
    const currentExpiry = deriveExpiry(user);
    const base = currentExpiry && currentExpiry > now ? currentExpiry : now;
    const expiry = new Date(base.getTime() + days * 24 * 60 * 60 * 1000);
    await updateDoc(doc(db, "users", user.id), {
      boostActive: true,
      boostExpiresAt: expiry,
      boostedUntil: expiry,
      boostedAt: now,
    });
    fetchUsers();
  };

  const adminUnboost = async (userId) => {
    await updateDoc(doc(db, "users", userId), {
      boostActive: false,
      boostExpiresAt: null,
      boostedUntil: null,
    });
    fetchUsers();
  };

  const handleLogout = async () => {
    await signOut(auth);
    navigate("/login");
  };

  if (loadingAuth)
    return <div className="flex justify-center items-center h-screen">⏳ Loading...</div>;
  if (currentUser?.suspended)
    return <SuspendedModal isOpen={isSuspendedOpen} onClose={() => {}} />;

  const filteredUsers = users.filter((user) =>
    user.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(filteredUsers.length / USERS_PER_PAGE);
  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * USERS_PER_PAGE,
    currentPage * USERS_PER_PAGE
  );

  const investors = paginatedUsers.filter((u) => u.role?.toLowerCase() === "investor");
  const founders = paginatedUsers.filter(
    (u) => !u.role || u.role?.toLowerCase() === "founder"
  );

  const goToPage = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) setCurrentPage(pageNumber);
  };

  /* ---------------- Yoco Payment Frontend Integration ---------------- */
  const getYocoToken = () =>
    new Promise((resolve, reject) => {
      if (!window.YocoSDK) {
        reject(new Error("Yoco SDK not loaded"));
        return;
      }

      const yoco = new window.YocoSDK({
        publicKey: process.env.REACT_APP_YOCO_PUBLIC_KEY, // Set in .env
      });

      yoco.showPaymentPage({
        amountInCents: 199900, // 7-day boost amount
        currency: "ZAR",
        name: "InvestorLanes Boost",
        description: "7-day boost for investor",
        onComplete: (result) => {
          if (result.error) {
            reject(new Error(result.error.message || "Payment failed"));
          } else {
            resolve(result.id); // Yoco token ID
          }
        },
      });
    });

  /* ---------------- Campaign History ---------------- */
  const CampaignHistory = ({ investorId }) => {
    const [rewards, setRewards] = useState([]);
    const [show, setShow] = useState(false);

    useEffect(() => {
      const fetchRewards = async () => {
        const q = query(collection(db, "rewards"), where("investorId", "==", investorId));
        const snapshot = await getDocs(q);
        let rewardList = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        rewardList.sort((a, b) => {
          const timeA = a.rewardedAt?.toDate ? a.rewardedAt.toDate().getTime() : 0;
          const timeB = b.rewardedAt?.toDate ? b.rewardedAt.toDate().getTime() : 0;
          return timeB - timeA;
        });
        setRewards(rewardList);
      };
      if (show) fetchRewards();
    }, [investorId, show]);

    return (
      <div className="mt-1">
        <button
          onClick={() => setShow(!show)}
          className="text-blue-600 hover:underline text-sm"
        >
          {show ? "Hide Reward History" : "View Reward History"}
        </button>
        {show && rewards.length > 0 && (
          <ul className="text-xs text-gray-700 mt-1 max-h-40 overflow-y-auto border rounded p-1">
            {rewards.map((r) => (
              <li key={r.id}>
                💵 {r.rewardAmount} {r.currency} for founder {r.founderId} on{" "}
                {r.rewardedAt?.toDate().toLocaleDateString() || "N/A"}
              </li>
            ))}
          </ul>
        )}
        {show && rewards.length === 0 && (
          <div className="text-xs text-gray-500 mt-1">No rewards yet.</div>
        )}
      </div>
    );
  };

  const getCountdown = (boostedUntil) => {
    const totalSeconds = Math.floor((boostedUntil - now) / 1000);
    if (totalSeconds <= 0) return null;
    const days = Math.floor(totalSeconds / (24 * 3600));
    const hours = Math.floor((totalSeconds % (24 * 3600)) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${days}d ${hours}h ${minutes}m ${seconds}s`;
  };

  /* ---------------- Render Users Table ---------------- */
  const renderTable = (userList, title) => (
    <>
      <h2 className="text-xl font-bold mt-8 mb-2">{title}</h2>
      <table className="w-full border text-sm rounded-xl overflow-hidden">
        <thead className="bg-gray-100">
          <tr>
            <th className="p-2 text-left">Email</th>
            <th className="p-2 text-left">Role</th>
            <th className="p-2 text-left">Plan</th>
            <th className="p-2 text-left">Currency</th>
            <th className="p-2 text-left">Status</th>
            <th className="p-2 text-left">Campaign</th>
            <th className="p-2 text-left">Balance</th>
            <th className="p-2 text-left">Joined</th>
            <th className="p-2 text-left">Actions</th>
          </tr>
        </thead>
        <tbody>
          {userList.map((user) => {
            const roleLabel =
              user.role?.toLowerCase() === "investor"
                ? "Investor"
                : user.role?.toLowerCase() === "founder"
                ? "Founder"
                : "Founder (Default)";

            const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
            const expFromExpiresAt = user.boostExpiresAt?.toDate
              ? user.boostExpiresAt.toDate()
              : user.boostExpiresAt
              ? new Date(user.boostExpiresAt)
              : null;
            const expFromBoostedUntil = user.boostedUntil?.toDate
              ? user.boostedUntil.toDate()
              : user.boostedUntil?.seconds
              ? new Date(user.boostedUntil.seconds * 1000)
              : user.boostedUntil
              ? new Date(user.boostedUntil)
              : null;
            const expFromBoostedAtPlus7 = user.boostedAt?.toDate
              ? new Date(user.boostedAt.toDate().getTime() + sevenDaysMs)
              : user.boostedAt?.seconds
              ? new Date(user.boostedAt.seconds * 1000 + sevenDaysMs)
              : null;
            const expiryCandidates = [expFromExpiresAt, expFromBoostedUntil, expFromBoostedAtPlus7].filter(Boolean);
            expiryCandidates.sort((a, b) => b - a);
            const effectiveExpiry = expiryCandidates[0] || null;
            const isBoostActive = effectiveExpiry && now < effectiveExpiry;

            return (
              <tr key={user.id} className="border-t hover:bg-gray-50">
                <td className="p-2 flex items-center gap-2">
                  {user.email}
                  {roleLabel === "Investor" && user.goldTier && (
                    <span className="bg-yellow-400 text-white text-xs px-1 rounded">
                      🌟 Gold
                    </span>
                  )}
                </td>
                <td className="p-2 capitalize">{roleLabel}</td>
                <td className="p-2 capitalize">{user.plan || "None"}</td>
                <td className="p-2">
                  {roleLabel === "Investor" ? (
                    <select
                      value={user.currency || "ZAR"}
                      onChange={(e) => updateCurrency(user.id, e.target.value)}
                      className="border px-2 py-1 rounded"
                    >
                      {currencyOptions.map((cur) => (
                        <option key={cur} value={cur}>
                          {cur}
                        </option>
                      ))}
                    </select>
                  ) : (
                    "-"
                  )}
                </td>
                <td className="p-2">{user.suspended ? "❌ Suspended" : "✅ Active"}</td>
                <td className="p-2">
                  {roleLabel === "Investor" && (
                    <button
                      onClick={() => toggleCampaign(user.id, user.campaignActive)}
                      className={`px-2 py-1 rounded text-white ${
                        user.campaignActive ? "bg-green-600" : "bg-gray-600"
                      }`}
                    >
                      {user.campaignActive ? "💰 Campaign Active" : "Start Campaign"}
                    </button>
                  )}
                </td>
                <td className="p-2 text-yellow-700 font-bold">
                  {roleLabel === "Investor"
                    ? `${user.balance || 0} ${user.currency || "ZAR"}`
                    : "-"}
                </td>
                <td className="p-2">
                  {user.updatedAt
                    ? new Date(user.updatedAt.seconds * 1000).toLocaleDateString()
                    : "N/A"}
                </td>
                <td className="p-2 space-y-1">
                  <select
                    value={user.plan || ""}
                    onChange={(e) => assignPlan(user.id, e.target.value)}
                    className="border px-2 py-1 rounded w-full"
                  >
                    <option value="">Select Plan</option>
                    <option value="starter">💼 Starter</option>
                    <option value="pro">📈 Pro</option>
                    <option value="unlimited">🌍 Unlimited</option>
                  </select>

                  <button
                    onClick={() => toggleSuspension(user.id, user.suspended)}
                    className={`mt-1 w-full px-2 py-1 text-white rounded ${
                      user.suspended ? "bg-red-600" : "bg-green-600"
                    }`}
                  >
                    {user.suspended ? "🚫 Suspended" : "Suspend / Reactivate"}
                  </button>

                  {roleLabel === "Investor" && (
                    <>
                      <button
                        onClick={async () => {
                          await updateDoc(doc(db, "users", user.id), {
                            verified: !user.verified,
                          });
                          fetchUsers();
                        }}
                        className={`mt-1 w-full px-2 py-1 text-white ${
                          user.verified ? "bg-gray-600" : "bg-green-600"
                        } rounded`}
                      >
                        {user.verified ? "❌ Unverify" : "✔️ Verify"}
                      </button>

                      {/* Boost Button (Yoco) */}
                      <button
                        onClick={async () => {
                          if (isBoostActive) return;

                          try {
                            const token = await getYocoToken();

                            const createBoostPayment = httpsCallable(
                              functions,
                              "createYocoPayment"
                            );
                            await createBoostPayment({
                              token,
                              planId: "boost7",
                              userId: user.id,
                            });

                            fetchUsers();
                            alert("💰💎 Boost activated for 7 days!");
                          } catch (err) {
                            console.error("Boost payment failed:", err);
                            alert("Payment failed. Please try again.");
                          }
                        }}
                        className={`mt-1 w-full px-2 py-1 text-white rounded ${
                          isBoostActive ? "bg-gray-600" : "bg-green-600"
                        }`}
                      >
                        {isBoostActive ? "💰💎 Boost Active" : "💰💎 Boost Now (7 days)"}
                      </button>

                      {/* Admin Manual Boost Controls */}
                      <button
                        onClick={() => adminBoost(user, 7)}
                        className="mt-1 w-full px-2 py-1 text-white rounded bg-purple-600 hover:bg-purple-700"
                        title="Admin-override: grant 7 days boost"
                      >
                        ⚡ Admin Boost 7 days
                      </button>
                      <button
                        onClick={() => adminUnboost(user.id)}
                        className="mt-1 w-full px-2 py-1 text-white rounded bg-gray-700 hover:bg-gray-800"
                        title="Remove boost immediately"
                      >
                        ⛔ Remove Boost
                      </button>

                      {isBoostActive && effectiveExpiry && (
                        <div className="mt-1 text-xs text-yellow-700 font-bold">
                          ⏱️ {getCountdown(effectiveExpiry)} remaining
                        </div>
                      )}

                      <button
                        onClick={async () => {
                          await updateDoc(doc(db, "users", user.id), {
                            goldTier: !user.goldTier,
                          });
                          fetchUsers();
                        }}
                        className={`mt-1 w-full px-2 py-1 text-white rounded ${
                          user.goldTier ? "bg-yellow-600" : "bg-gray-600"
                        }`}
                      >
                        {user.goldTier ? "🌟 Remove Gold-tier" : "🌟 Grant Gold-tier"}
                      </button>

                      <CampaignHistory investorId={user.id} />
                    </>
                  )}

                  {roleLabel.includes("Founder") && (
                    <>
                      <div className="mt-1 text-xs text-gray-600">
                        Contacts Remaining: <strong>{user.contactsRemaining || 0}</strong>
                      </div>
                      <div className="flex items-center gap-1 mt-1">
                        <input
                          type="number"
                          min="0"
                          placeholder="Set number"
                          className="border rounded px-2 py-1 w-20 text-sm"
                          value={contactValues[user.id] || ""}
                          onChange={(e) =>
                            setContactValues((prev) => ({
                              ...prev,
                              [user.id]: e.target.value,
                            }))
                          }
                          onKeyDown={async (e) => {
                            if (e.key === "Enter") handleSetContacts(user.id);
                          }}
                        />
                        <button
                          className="bg-green-600 text-white px-2 py-1 rounded text-sm"
                          onClick={() => handleSetContacts(user.id)}
                        >
                          ✅
                        </button>
                      </div>
                    </>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </>
  );

  return (
    <div className="max-w-6xl mx-auto px-4 py-8 relative">
      <button
        onClick={handleLogout}
        className="absolute top-4 right-4 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded"
      >
        🚪 Logout
      </button>

      <div className="mb-4 text-sm text-gray-600 flex items-center gap-1">
        <span className="text-gray-500">🏠 Dashboard</span>
        <span className="mx-1">›</span>
        <Link
          to="/admin/analytics"
          className="text-blue-600 hover:text-purple-700 hover:underline transition-colors duration-200"
        >
          📊 Analytics
        </Link>
      </div>

      <h1 className="text-3xl font-bold mb-6">🛡️ Admin Dashboard</h1>

      <input
        type="text"
        placeholder="🔍 Search by email..."
        value={searchTerm}
        onChange={(e) => {
          setSearchTerm(e.target.value);
          setCurrentPage(1);
        }}
        className="mb-4 px-4 py-2 border rounded w-full"
      />

      {filteredUsers.length === 0 ? (
        <p className="text-gray-500">No users found...</p>
      ) : (
        <>
          {renderTable(investors, "📈 Investors")}
          {renderTable(founders, "🚀 Founders")}

          <div className="mt-6 flex justify-center items-center space-x-2">
            <button
              className="px-3 py-1 bg-gray-300 rounded disabled:opacity-50"
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1}
            >
              ⬅️ Prev
            </button>
            <span>
              Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
            </span>
            <button
              className="px-3 py-1 bg-gray-300 rounded disabled:opacity-50"
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Next ➡️
            </button>
          </div>
        </>
      )}

      <SuspendedModal
        isOpen={isSuspendedOpen}
        onClose={() => setIsSuspendedOpen(false)}
      />
    </div>
  );
};

export default AdminDashboard;
